import { ApiKeysServiceAPI } from '@API/data-services/api-keys-service'
import {
  BackOfficeApiKeyRecord,
  BackOfficeApiKeyScopeUsageRecord,
} from '@Generated/back-office-client/src'
import { Badge, Callout, Table, Text } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'

interface ScopeUsageSectionProps {
  apiKey: BackOfficeApiKeyRecord
}

export const ScopeUsageSection = ({ apiKey }: ScopeUsageSectionProps) => {
  const enabled = !apiKey.fullAccess && apiKey.scopes.length > 0

  const {
    data: usageRecords,
    isLoading: loading,
    isError: error,
  } = useQuery<BackOfficeApiKeyScopeUsageRecord[]>({
    queryKey: ['scope-usage', apiKey.id, apiKey.service],
    queryFn: () => ApiKeysServiceAPI.getScopeUsage(apiKey.id, apiKey.service),
    enabled,
  })

  if (apiKey.fullAccess) {
    return (
      <Callout.Root color="blue">
        <Callout.Text>Full Access — per-scope tracking not applicable.</Callout.Text>
      </Callout.Root>
    )
  }

  if (apiKey.scopes.length === 0) {
    return (
      <Text size="2" color="gray">
        No scopes assigned to this key.
      </Text>
    )
  }

  if (loading) {
    return (
      <Text size="2" color="gray">
        Loading scope usage…
      </Text>
    )
  }

  if (error) {
    return (
      <Callout.Root color="red">
        <Callout.Text>Failed to load scope usage data.</Callout.Text>
      </Callout.Root>
    )
  }

  if (!usageRecords) {
    return null
  }

  return (
    <Table.Root variant="surface">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Scope</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Last Used</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {usageRecords.map((record) => (
          <Table.Row key={record.scope}>
            <Table.Cell>
              <Text size="2">{record.scope}</Text>
            </Table.Cell>
            <Table.Cell>
              {record.lastUsedAt !== null ? (
                <Badge color="green">Used</Badge>
              ) : (
                <Badge color="amber">Never Used</Badge>
              )}
            </Table.Cell>
            <Table.Cell>
              <Text size="2">
                {record.lastUsedAt !== null ? new Date(record.lastUsedAt).toLocaleString() : '—'}
              </Text>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}
